import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import { CardSetTransformationService, CoreService, DataService, DialogService, FormConfigurationService, SaveDataRequest, SaveFormEvent, SpinnerService } from 'dspx-core';
import { finalize, take } from 'rxjs';
import { API_ENDPOINTS, API_ENDPOINT_KEYS } from '../../constants/api-endpoints.constants';
import { NAV_TAB_NUMBERS, PAGE_INDEX, PAGE_NUMBERS, POP_UP_CODES, QUESTION_CODES, REFERENCE_DATA_IDS, SAVE_RESULT_STATUS, SECTION_CODES, SECTION_INDEX, SERVER_ERROR_POP_UP_CODES } from '../../constants/app-data.constants';
import { ROUTE_CONSTANTS } from '../../constants/route.constants';
import { TEMPLATES, VERSIONS } from '../../constants/templates.constants';
import { AppDataService } from '../../services/app-data.service';
import { ErrorService } from '../../services/error.service';
import { BaseCardSetDetailComponent } from '../base-card-set-detail-component/base-card-set-detail.component';

@Component({
  selector: 'app-gl-string-lookup-mapping-set',
  templateUrl: './gl-string-lookup-mapping-set.component.html',
  styleUrl: './gl-string-lookup-mapping-set.component.scss'
})
export class GLStringLookupMappingSetComponent extends BaseCardSetDetailComponent implements OnInit, OnDestroy, AfterViewInit {
  fieldTypeReplacements = CardSetTransformationService.cardSetWithFlatTabsFieldTypeReplacements;
  formServiceEndpoint: string = API_ENDPOINT_KEYS.getFormConfig;

  templateId: number = TEMPLATES.glStringLookupMappingSet;
  versionNo: number = VERSIONS.latest;
  model: any;
  activeDialog: string = '';
  id: string = '';
  isEditing: boolean = false;

  constructor(protected router: Router,
    protected route: ActivatedRoute,
    protected appDataService: AppDataService,
    protected dialogService: DialogService,
    private coreService: CoreService,
    private spinnerService: SpinnerService,
    private dataService: DataService,
    private errorService: ErrorService,
    private formConfigurationService: FormConfigurationService,
  ) {
    super(router, appDataService, dialogService);
  }

  ngOnInit(): void {
    this.coreService.applicationFunctions = {};
    this.coreService.conditionFunctions = {
      showDefaultReturnValue: () => this.showDefaultReturnValue(),
      showDefaultReturnValueLookup: () => this.showDefaultReturnValueLookup()
    };

    this.route.paramMap.subscribe((paramMap: ParamMap) => {
      const id = paramMap.get('id');
      if (id) {
        this.id = id;
        this.formServiceEndpoint = API_ENDPOINT_KEYS.getGlStringLookupMappingSetConfigAndResponse;
        this.isEditing = true;
      }
    });
  }

  ngAfterViewInit(): void {
  }

  ngOnDestroy(): void {
    this.unsubscribeSubscriptions();
    this.coreService.applicationFunctions = {};
    this.coreService.repeatingPageFunctions = {};
    this.coreService.repeatingSectionFunctions = {};
  }

  formLoaded(event: any): void {
  }

  modelChanged(event: any): void {
  }

  filter(event: any): void {
  }

  buttonClicked(event: any): void {
  }

  save(event: SaveFormEvent) {
    const saveDataRequest: SaveDataRequest = {
      template: this.templateId,
      versionNumber: this.versionNo,
      id: this.id,
      response: event.model,
      expectedLastUpdateTime: event.lastUpdatedTime,
      isSubmit: event.isSubmit,
    }
    if (!event.isFormValid) {
      this.baseForm.markAllFieldsAsTouched();
      if (!this.baseForm.fields[0]?.formControl.valid) {
        this.baseForm.showValidationSummary(true);
        this.dialogService.showDialog(POP_UP_CODES.common.validationError);
      }
      else {
        this.saveData(saveDataRequest);
      }
    }
    else {
      this.saveData(saveDataRequest);
    }
  }

  cancel(event: any) {
    this.exit();
  }

  actionButton_lookupMappingGrid_datagrid_new_Clicked(event: any) {
    this.navigate(['/', ROUTE_CONSTANTS.glStringLookupMappingSet.prefix, this.id, ROUTE_CONSTANTS.glStringLookupMapping.prefix]);
  }

  actionButton_lookupMappingGrid_datagrid_edit_Clicked(event: any) {
    this.navigate(['/', ROUTE_CONSTANTS.glStringLookupMappingSet.prefix, this.id, ROUTE_CONSTANTS.glStringLookupMapping.prefix, event.event.data.id]);
  }

  dialogButton_saveSuccessful_Clicked(event: any) {
    this.exit(true);
  }

  private exit(ignoreUnsavedChanges = false): void {
    this.navigate(['/', ROUTE_CONSTANTS.glStringLookupMappingSet.list], ignoreUnsavedChanges);
  }

  private saveData(request: SaveDataRequest) {
    this.spinnerService.showSpinner();
    this.registerSubscription(
      this.dataService.saveData(API_ENDPOINTS.glStringLookupMappingSet.save, request)
        .pipe(
          take(1),
          this.errorService.errorHandler(),
          finalize(() => this.spinnerService.hideSpinner())
        )
        .subscribe(data => {
          if (data?.status === SAVE_RESULT_STATUS.success) {
            if (SERVER_ERROR_POP_UP_CODES.saveGlStringLookupMappingSet.includes(data.message)) {
              this.activeDialog = data.message;
            }
            else {
              this.id = data.id;
              this.activeDialog = POP_UP_CODES.common.saveSuccessful;
            }
            this.dialogService.showDialog(this.activeDialog);
          }
        })
    );
  }

  private getDefaultReturnValueTypeId(): string {
    return this.formConfigurationService.getQuestionResponse(
      this.baseForm.model.form.tabs,
      'tab' + NAV_TAB_NUMBERS.glStringLookupMappingSetForm.glStringLookupMappingSet,
      'page' + PAGE_NUMBERS.glStringLookupMappingSetForm.glStringLookupMappingSet,
      PAGE_INDEX.noRepeater,
      SECTION_CODES.glStringLookupMappingSetForm.lookupMappingSetSection,
      SECTION_INDEX.noRepeater,
      QUESTION_CODES.glStringLookupMappingSetForm.defaultReturnValueTypeId
    );
  }

  private showDefaultReturnValue(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const defaultReturnValueTypeId = this.getDefaultReturnValueTypeId();

      return defaultReturnValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.segment;
    }

    return false;
  }

  private showDefaultReturnValueLookup(): boolean {
    if (this.baseForm?.model?.form?.tabs != null) {
      const defaultReturnValueTypeId = this.getDefaultReturnValueTypeId();

      return defaultReturnValueTypeId === REFERENCE_DATA_IDS.glStringSegmentValueType.lookup;
    }

    return false;
  }
}
